/**
 * ConfirmDialog — themed confirmation modal
 *
 * Props:
 *   open          – boolean
 *   onClose       – () => void
 *   onConfirm     – () => void
 *   title         – string          (default: "Are you sure?")
 *   message       – string | ReactNode
 *   note          – string | ReactNode   optional info hint shown in a subtle box
 *   confirmLabel  – string          (default: "Confirm")
 *   cancelLabel   – string          (default: "Cancel")
 *   variant       – 'danger' | 'warning' | 'default'
 *   loading       – boolean
 */

import { useEffect } from 'react';
import Button from './Button';

const VARIANTS = {
  danger: {
    iconBg: '#f2e9e6',
    iconColor: '#9a564a',
    hoverBg: '#9a564a',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
      </svg>
    ),
  },
  warning: {
    iconBg: '#fffbeb',
    iconColor: '#d97706',
    hoverBg: '#d97706',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  default: {
    iconBg: '#e8f4f4',
    iconColor: '#145f5f',
    hoverBg: '#145f5f',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
};

function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message = '',
  note = null,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, loading]);

  if (!open) return null;

  const v = VARIANTS[variant] || VARIANTS.default;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,17,31,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
    >
      <div
        className="relative bg-white rounded-2xl w-[400px] max-w-[92vw] overflow-hidden"
        style={{ boxShadow: '0 12px 40px rgba(8,17,31,0.16), 0 2px 8px rgba(8,17,31,0.06)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-3 right-3 flex items-center justify-center w-7 h-7 rounded-full transition-colors disabled:opacity-40"
          style={{ color: '#9ca3af' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Body */}
        <div className="p-4">
          {/* Icon */}
          <div
            className="flex items-center justify-center rounded-xl mb-3"
            style={{ width: 48, height: 48, background: v.iconBg, color: v.iconColor }}
          >
            {v.icon}
          </div>

          {/* Title */}
          <div className="text-[17px] font-bold mb-1" style={{ color: '#08111f', fontFamily: "'Cormorant Garamond', serif" }}>
            {title}
          </div>

          {/* Message */}
          {message && (
            <div className="text-[13px] leading-[1.5] mb-3" style={{ color: '#7a7060' }}>
              {message}
            </div>
          )}

          {/* Note box */}
          {note && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-lg mb-3 text-[12px] leading-snug"
              style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#7a7060' }}
            >
              <InfoIcon />
              <span>{note}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center gap-2 mt-1">
            <Button
              variant="ghost"
              size="lg"
              className="flex-1"
              onClick={onClose}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={variant === 'danger' ? 'danger' : variant === 'warning' ? 'warning' : 'ink'}
              size="lg"
              className="flex-1"
              onClick={onConfirm}
              loading={loading}
            >
              {loading ? 'Working...' : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
